// 動畫時間軸：依排版結果決定每個元素何時出現。從中心主題開始，沿父子連線一層層往外傳。
// 動態 SVG、編輯器播放、之後的簡報模式與影片匯出都共用這條時間軸。
import type { MapLayout } from './layout.js';

export interface Span {
  /** 開始時間（秒） */
  start: number;
  /** 持續時間（秒） */
  duration: number;
}

export interface Timeline {
  /** 節點彈出 */
  nodes: Map<string, Span>;
  /** 連到該節點的父子連線（以子節點 id 為 key） */
  edges: Map<string, Span>;
  /** 外框淡入（以擁有外框的節點 id 為 key） */
  boundaries: Map<string, Span>;
  /** 關聯線，順序同 layout.relations */
  relations: Span[];
  /** 子節點在父節點中的深度（中心為 0），流動光點依此錯開 */
  depth: Map<string, number>;
  /** 所有建構動畫結束的時間 */
  end: number;
}

export interface TimelineOptions {
  /** 建構動畫總長上限（秒）；超過時整體等比例縮短 */
  maxDuration?: number;
}

const NODE_DURATION = 0.45;
const SIBLING_STAGGER = 0.08;
const BOUNDARY_DURATION = 0.5;
const RELATION_DURATION = 0.7;

const round = (value: number) => Math.round(value * 100) / 100;

// 主要入口。
export function buildTimeline(layout: MapLayout, options: TimelineOptions = {}): Timeline {
  const maxDuration = options.maxDuration ?? 7;
  const nodes = new Map<string, Span>();
  const edges = new Map<string, Span>();
  const depth = new Map<string, number>();
  const children = new Map<string, string[]>();
  const lengths = new Map<string, number>();
  for (const edge of layout.edges) {
    children.set(edge.from, [...(children.get(edge.from) ?? []), edge.to]);
    const first = edge.points[0]!;
    const last = edge.points[edge.points.length - 1]!;
    lengths.set(edge.to, Math.hypot(last.x - first.x, last.y - first.y));
  }

  const root = layout.nodes[0];
  if (!root) return { nodes, edges, boundaries: new Map(), relations: [], depth, end: 0 };

  // 深度優先：子節點在父節點彈出到一半時開始畫連線，兄弟之間稍微錯開。
  const visit = (id: string, start: number, level: number) => {
    nodes.set(id, { start, duration: NODE_DURATION });
    depth.set(id, level);
    (children.get(id) ?? []).forEach((child, index) => {
      const edgeStart = start + NODE_DURATION * 0.6 + index * SIBLING_STAGGER;
      const edgeDuration = Math.min(0.6, Math.max(0.3, (lengths.get(child) ?? 0) / 500));
      edges.set(child, { start: edgeStart, duration: edgeDuration });
      visit(child, edgeStart + edgeDuration * 0.85, level + 1);
    });
  };
  visit(root.id, 0, 0);

  // 外框：整個子樹出現後淡入。
  const subtreeEnd = (id: string): number => {
    const own = nodes.get(id);
    const ownEnd = own ? own.start + own.duration : 0;
    return Math.max(ownEnd, ...(children.get(id) ?? []).map(subtreeEnd));
  };
  const boundaries = new Map<string, Span>();
  for (const boundary of layout.boundaries) boundaries.set(boundary.id, { start: subtreeEnd(boundary.id) + 0.1, duration: BOUNDARY_DURATION });

  // 關聯線：所有節點都出現後才畫。
  const nodesEnd = Math.max(0, ...[...nodes.values()].map(span => span.start + span.duration));
  const relations = layout.relations.map((_, index) => ({ start: nodesEnd + 0.15 + index * 0.2, duration: RELATION_DURATION }));

  let end = Math.max(nodesEnd, ...[...boundaries.values(), ...relations].map(span => span.start + span.duration));

  // 太長時整體等比例縮短（持續時間縮得比較少，避免動作太急）。
  if (end > maxDuration) {
    const factor = maxDuration / end;
    const scale = (span: Span): Span => ({ start: span.start * factor, duration: Math.max(span.duration * Math.sqrt(factor), 0.2) });
    for (const map of [nodes, edges, boundaries]) for (const [key, span] of map) map.set(key, scale(span));
    relations.forEach((span, index) => { relations[index] = scale(span); });
    end = Math.max(...[...nodes.values(), ...edges.values(), ...boundaries.values(), ...relations].map(span => span.start + span.duration));
  }

  const tidy = (span: Span): Span => ({ start: round(span.start), duration: round(span.duration) });
  for (const map of [nodes, edges, boundaries]) for (const [key, span] of map) map.set(key, tidy(span));
  return { nodes, edges, boundaries, relations: relations.map(tidy), depth, end: round(end) };
}
